import memoize from 'fast-memoize';
import { Moment } from 'moment';
import { $HierarchicalFilterSeparator$ } from '../api/api-interfaces';
import { Granularity, Months } from '../date-manager/date-manager-constants';
import {
  DateManagerService,
  dateManagerService,
} from '../date-manager/date-manager-service';
import {
  getEndOfFiscalYear,
  getEndOfQuarter,
  getStartOfFiscalYear,
  getStartOfQuarter,
} from '../date-manager/date-manager-utils';
import { chartColors, genderColorMap } from '../theme/color-constants';
import { VersionId } from '../v2/api/types';
import { Filter } from '../v2/common/components/filter/filterbar/types';
import { HierarchyItem } from '../v2/common/components/filter/types';
import {
  ApiMasterDataQueryFilterItem,
  DataFields,
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
  Operations,
} from '../v2/types';
import { PeriodRange, TimeSliderConfig } from './filter-store';

export const getEndOfMonth = (date: Moment) => {
  return dateManagerService
    .getInstance()
    .formatDateApi(date.clone().endOf(Granularity.MONTH));
};

export const getStartOfMonth = (date: Moment) => {
  return dateManagerService
    .getInstance()
    .formatDateApi(date.clone().startOf(Granularity.MONTH));
};

const combineFiltersConstructor = (filters: ApiMasterDataQueryFilterItem[]) => {
  const filtersCopy: ApiMasterDataQueryFilterItem[] = JSON.parse(
    JSON.stringify(filters || [])
  );
  const filterItems: ApiMasterDataQueryFilterItem[] = [];
  filtersCopy.forEach((filter: ApiMasterDataQueryFilterItem) => {
    const foundFilter = filterItems.find(
      (item) =>
        item.property === filter.property &&
        item.operation === filter.operation &&
        item.dataType === filter.dataType &&
        item.operation !== Operations.NOT_EQUAL &&
        filter.operation !== Operations.NOT_EQUAL &&
        item.dontCombine !== true &&
        filter.dontCombine !== true
      // for not equal, or doesn't make sense
      // (x != y || x != z) will always be true if y and z are different
    );

    if (foundFilter) {
      foundFilter.values = Array.from(
        new Set([...foundFilter.values, ...filter.values])
      );
    } else {
      filterItems.push(filter);
    }
  });
  return filterItems;
};

export const combineFilters = memoize(combineFiltersConstructor);

export const getVersionFilterFromDate = (
  date: Moment,
  firstMonthOfYear: Months,
  latestVersion: VersionId,
  dataType: DataTypes = DataTypes.EMPLOYEE
): ApiMasterDataQueryFilterItem => {
  const version = getApplicableVersionForDate(
    date,
    firstMonthOfYear,
    latestVersion
  );
  return {
    operation: Operations.EQUAL,
    property: 'VERSION_ID',
    values: [version],
    dataType,
  };
};

export const getVersionFilterFromDateRange = (
  dates: string[],
  dataType: DataTypes = DataTypes.EMPLOYEE
): ApiMasterDataQueryFilterItem => {
  return {
    operation: Operations.EQUAL,
    property: 'VERSION_ID',
    values: dates,
    dataType,
  };
};

export const endOfDate = (
  date: Moment,
  unitOfTime: Granularity,
  firstMonthOfYear: Months
) => {
  const dateCopy = date.clone();
  if (unitOfTime === Granularity.FINQUARTER) {
    return getEndOfQuarter(dateCopy, firstMonthOfYear);
  } else if (unitOfTime === Granularity.FINYEAR) {
    return getEndOfFiscalYear(dateCopy, firstMonthOfYear);
  }
  return dateCopy.endOf(unitOfTime);
};

export const isSameDate = (
  firstDate: Moment,
  secondDate: Moment,
  granularity: Granularity,
  firstMonthOfYear: Months
): boolean => {
  if (granularity === Granularity.FINYEAR) {
    const endOfFinYear1 = endOfDate(firstDate, granularity, firstMonthOfYear);
    const endOfFinYear2 = endOfDate(secondDate, granularity, firstMonthOfYear);
    return isSameDate(
      endOfFinYear1,
      endOfFinYear2,
      Granularity.MONTH,
      firstMonthOfYear
    );
  }
  return firstDate.isSame(secondDate, getRawBenchmarkGranularity(granularity));
};

export const startOfDate = (
  date: Moment,
  unitOfTime: Granularity,
  firstMonthOfYear: Months
) => {
  const dateCopy = date.clone();
  if (unitOfTime === Granularity.FINQUARTER) {
    return getStartOfQuarter(date, firstMonthOfYear);
  } else if (unitOfTime === Granularity.FINYEAR) {
    return getStartOfFiscalYear(date, firstMonthOfYear);
  }
  return dateCopy.startOf(unitOfTime);
};

const addUnitGranularityToDate = (date: Moment, granularity: Granularity) => {
  if (granularity === Granularity.FINYEAR) {
    return date.add(12, Granularity.MONTH).endOf(Granularity.MONTH);
  }
  return date.add(1, getRawBenchmarkGranularity(granularity));
};

export const getDatesInRange = (
  timeSliderConfig: TimeSliderConfig,
  providedDateManagerService: DateManagerService = dateManagerService.getInstance()
) => {
  const {
    startDate,
    endDate,
    granularity = Granularity.MONTH,
    firstMonthOfYear,
  } = timeSliderConfig;
  if (!startDate || !endDate) {
    throw new Error('Undefined dates found in getDatesInRange function');
  }
  const results = [];
  const { parseApiDate, formatDateApi } = providedDateManagerService;
  const minStartDate = parseApiDate(startDate);
  const maxEndDate = parseApiDate(endDate);
  let date = minStartDate.clone();
  while (
    endOfDate(date, granularity, firstMonthOfYear).isSameOrBefore(
      maxEndDate,
      Granularity.DAY
    )
  ) {
    date = endOfDate(date, granularity, firstMonthOfYear);
    results.push(formatDateApi(date));
    date = addUnitGranularityToDate(date, granularity);
  }
  if (
    maxEndDate
      .clone()
      .isBefore(
        endOfDate(maxEndDate, granularity, firstMonthOfYear),
        Granularity.DAY
      )
  ) {
    results.push(formatDateApi(maxEndDate));
  }
  return results;
};

export const getMonthlyVersionsInLatestGranularityPeriod = (
  timeSliderConfig: TimeSliderConfig
) => {
  // eg - for quarter granularity, get all monthly versions in latest quarter
  // useful for cases such as payroll where we need total value in quarter or year
  const { endDate, granularity, firstMonthOfYear } = timeSliderConfig;
  const { formatDateApi, parseApiDate } = dateManagerService.getInstance();
  const startOfPeriod = formatDateApi(
    startOfDate(parseApiDate(endDate), granularity, firstMonthOfYear)
  );
  const monthlyVersionsInPeriod = getDatesInRange({
    ...timeSliderConfig,
    startDate: startOfPeriod,
    granularity: Granularity.MONTH,
  });
  return monthlyVersionsInPeriod;
};

export const getMonthStartAndMonthEndDatesInPeriod = (
  startDate: Moment,
  endDate: Moment,
  firstMonthOfYear: Months
) => {
  // This function should always return month start and month end dates and therefore doesn't need granularity
  const dates = [];
  let tempStartDate = startDate;
  const tempEndDate = endDate;
  const { formatDateApi } = dateManagerService.getInstance();
  while (tempStartDate.isBefore(tempEndDate, Granularity.MONTH)) {
    dates.push(
      formatDateApi(tempStartDate),
      formatDateApi(
        endOfDate(tempStartDate, Granularity.MONTH, firstMonthOfYear)
      )
    );
    tempStartDate = startOfDate(
      tempStartDate.add(1, Granularity.MONTH),
      Granularity.MONTH,
      firstMonthOfYear
    );
  }
  dates.push(formatDateApi(tempStartDate), formatDateApi(tempEndDate));
  return dates;
};

//TODO: move this to domainDependencyStore?
export const getApplicableVersionForDate = (
  date: Moment,
  firstMonthOfYear: Months,
  latestVersion: VersionId,
  granularity: Granularity = Granularity.MONTH
) => {
  const endDate = endOfDate(date, granularity, firstMonthOfYear);
  return endDate.isAfter(
    dateManagerService.getInstance().parseApiDate(latestVersion)
  )
    ? latestVersion
    : dateManagerService.getInstance().formatDateApi(endDate);
};

export const getPastDatesFromDate = (
  toDate: Moment,
  amount: number,
  unit: Granularity.MONTH | Granularity.YEAR | Granularity.DAY
) => {
  return toDate.subtract(amount, unit);
};

export const getChartColorsForByDimChart = (
  dimension: DataFieldWithDataType,
  chartType: string,
  labels: string[]
) => {
  let colors;
  labels = labels.map((l) => l || 'NA');
  if (chartType === 'doughnut') {
    if (dimension.dataField === 'GENDER') {
      colors = labels.map((label: string) => genderColorMap[label]);
    } else {
      colors = labels.map((_l: any, i: number) => {
        return chartColors[i % 13];
      });
    }
  } else {
    colors = chartColors[0];
  }
  return colors;
};

export const getChartColorsForIndex = (index: number) => {
  return chartColors[index % chartColors.length];
};

export const getQuarterMonthsUptilDate = (date: Moment) => {
  const monthNumInItsQuarter = getMonthNumInItsQuarter(date);
  let months: string[] = [dateManagerService.getInstance().formatDateApi(date)];
  for (let i = 1; i < monthNumInItsQuarter; i++) {
    const previousMonthDate = dateManagerService
      .getInstance()
      .formatDateApi(
        date.clone().subtract(i, Granularity.MONTH).endOf(Granularity.MONTH)
      );
    months = [previousMonthDate, ...months];
  }
  return months;
};

const getMonthNumInItsQuarter = (date: Moment) => {
  const monthNum = date.month() + 1;
  let monthNumInItsQuarter;
  if (monthNum % 3 === 0) {
    monthNumInItsQuarter = 3;
  } else if (monthNum % 3 === 1) {
    monthNumInItsQuarter = 1;
  } else {
    monthNumInItsQuarter = 2;
  }
  return monthNumInItsQuarter;
};

export const getSamePrecedingPeriodTimeSliderConfig = (
  currTimeSliderConfig: TimeSliderConfig
) => {
  const { startDate: currStartDate, endDate: currEndDate } =
    currTimeSliderConfig;
  const { parseApiDate, formatDateApi } = dateManagerService.getInstance();
  // assumes month granularity
  const numMonthsInPeriod =
    parseApiDate(currEndDate).diff(currStartDate, Granularity.MONTH) + 1;
  const prevStartDate = formatDateApi(
    parseApiDate(currStartDate)
      .subtract(numMonthsInPeriod, Granularity.MONTH)
      .startOf(Granularity.MONTH)
  );
  const prevEndDate = formatDateApi(
    parseApiDate(currEndDate)
      .subtract(numMonthsInPeriod, Granularity.MONTH)
      .endOf(Granularity.MONTH)
  );
  return {
    ...currTimeSliderConfig,
    startDate: prevStartDate,
    endDate: prevEndDate,
  };
};

export const getSpecificPeriodTimeSliderConfig = (
  specificPeriodRange: PeriodRange,
  timeSliderConfig: TimeSliderConfig
) => {
  const { startDate, endDate } = specificPeriodRange;
  return {
    ...timeSliderConfig,
    startDate,
    endDate,
  };
};

export const getRawBenchmarkGranularity = (granularity: Granularity) => {
  switch (granularity) {
    case Granularity.FINYEAR:
      return Granularity.YEAR;
    case Granularity.FINQUARTER:
      return Granularity.QUARTER;
    case Granularity.DAY:
    case Granularity.MONTH:
    case Granularity.WEEK:
    case Granularity.YEAR:
      return granularity;
  }
};
export const getTimeSliderConfigForQtAndYearBenchmark = (
  timeSliderConfig: TimeSliderConfig,
  benchmarkGranularity: Granularity
) => {
  const { startDate, endDate, granularity, firstMonthOfYear } =
    timeSliderConfig;
  const rawBenchmarkGranularity =
    getRawBenchmarkGranularity(benchmarkGranularity);
  const { formatDateApi, parseApiDate } = dateManagerService.getInstance();
  const benchmarkStartDate = formatDateApi(
    startOfDate(
      parseApiDate(startDate).subtract(1, rawBenchmarkGranularity),
      granularity,
      firstMonthOfYear
    )
  );
  // ^ Here I can probably get away with not doing start of year but I guess its safer to do it anyway
  // This assumes that the start date provided will always be start of granularity

  // Have to confirm how this handles things like leap year etc
  const benchmarkEndDate = formatDateApi(
    endOfDate(
      parseApiDate(endDate).subtract(1, rawBenchmarkGranularity),
      granularity,
      firstMonthOfYear
    )
  );
  // ^ Need this as current month might not be completed. Not sure what issues this could cause in the
  // future when we have weekly granualrity etc. Maybe not many
  const benchmarkTimeSliderConfig = {
    ...timeSliderConfig,
    startDate: benchmarkStartDate,
    endDate: benchmarkEndDate,
  };
  return benchmarkTimeSliderConfig;
};

export const twelveMonthsBeforeDate = (date: Moment) => {
  const { formatDateApi } = dateManagerService.getInstance();
  return formatDateApi(
    date.subtract(11, Granularity.MONTH).startOf(Granularity.MONTH)
  );
};

export const getPeriodStartDateFromStartDate = (
  startDate: Moment,
  granularity: Granularity = Granularity.MONTH,
  firstMonthOfYear: Months
) =>
  dateManagerService
    .getInstance()
    .formatDateApi(startOfDate(startDate, granularity, firstMonthOfYear));

export const convertFilterItemsToJoinersViewDataType = (
  filters: Filter[]
): ApiMasterDataQueryFilterItem[] => {
  return filters.map((f) => ({
    ...f,
    dataType:
      f.dataType === DataTypes.EMPLOYEE ? DataTypes.JOINERS_VIEW : f.dataType,
    sql: f.sql?.replace(/"EMPLOYEE"/g, '"JOINERS_VIEW"'),
  }));
};

const findHierarchicalItemInHierarchy = (
  filterValues: string[],
  filterHierarchy: HierarchyItem[]
): HierarchyItem | undefined => {
  let subTree = filterHierarchy;
  filterValues.front().forEach((filter) => {
    subTree = subTree?.find((tree) => tree.name === filter)?.subItems ?? [];
  });
  const foundItem = subTree.find((tree) => tree.name === filterValues.last());
  return foundItem;
};

export const getChildren = (
  filterString: string,
  filterHierarchy: HierarchyItem[]
): HierarchyItem[] => {
  const filterValues = filterString.split($HierarchicalFilterSeparator$);
  const subTree =
    findHierarchicalItemInHierarchy(filterValues, filterHierarchy)?.subItems ??
    [];
  return subTree;
};

export const getDimension = (
  dataType: DataTypes,
  dataField: DataFields
): DataFieldWithDataType => {
  return {
    dataType,
    dataField,
  };
};

export const getEmployeeDimension = (dataField: EmployeeDataFields) =>
  getDimension(DataTypes.EMPLOYEE, dataField);
